// Helpers for parsing WhatsApp message templates on the dashboard.
//
// A template's variables can live in more than one component - most commonly the BODY, but
// also a TEXT HEADER (e.g. "Thank you for your order {{order_id}}"). The backend maps each
// {{variable}} back to its component by name/position, so the parser must collect variables
// from every text-bearing parameterized component, not just the body.

// Component types whose {{variables}} are sent as template parameters.
const PARAMETERIZED_COMPONENT_TYPES = ['HEADER', 'BODY'];

const VARIABLE_REGEX = /{{([^}]+)}}/g;

export const stripVariableBraces = str => str.replace(/{{|}}/g, '');

// Text-bearing HEADER/BODY components, in template order (header before body).
export const getParameterizedComponents = template =>
  (template?.components || []).filter(
    component =>
      PARAMETERIZED_COMPONENT_TYPES.includes(component.type) &&
      typeof component.text === 'string'
  );

// All {{variable}} tokens across the parameterized components, in order of appearance.
export const getTemplateVariables = template =>
  getParameterizedComponents(template).flatMap(
    component => component.text.match(VARIABLE_REGEX) || []
  );

// Header and body number their positional variables separately, so a header {{1}} and a
// body {{1}} are different inputs. Header inputs are keyed with this prefix in the form.
export const HEADER_PARAM_PREFIX = 'header.';

const variablesOf = component =>
  (component.text.match(VARIABLE_REGEX) || []).map(stripVariableBraces);

// Builds the empty form map ({ variableName: '' }) the agent fills in. Body variables use
// their bare name, header variables the `header.` prefix.
export const buildProcessedParams = template =>
  getParameterizedComponents(template).reduce((acc, component) => {
    const prefix = component.type === 'HEADER' ? HEADER_PARAM_PREFIX : '';
    variablesOf(component).forEach(name => {
      acc[`${prefix}${name}`] = '';
    });
    return acc;
  }, {});

// Turns the form map into the processed_params payload: body values stay flat (the format
// the backend and API clients already use), header values go under `header`.
export const toProcessedParamsPayload = params =>
  Object.entries(params).reduce((acc, [key, value]) => {
    if (key.startsWith(HEADER_PARAM_PREFIX)) {
      acc.header = {
        ...acc.header,
        [key.slice(HEADER_PARAM_PREFIX.length)]: value,
      };
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});
